import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '../hooks/useAuth';

interface PlatformTenant {
  id: string;
  company_name: string;
  subdomain: string;
  subscription_tier: string;
  subscription_status: string;
  billing_email: string;
  custom_domain: string | null;
  max_users: number | null;
  max_client_tenants: number | null;
  storage_limit_gb: number | null;
  branding_config: any;
  created_at: string;
  updated_at: string;
}

export type UserRole = 'anonymous' | 'user' | 'customer_admin' | 'platform_admin' | 'super_admin';

interface PlatformTenantContextValue {
  platformTenant: PlatformTenant | null;
  isLoading: boolean;
  loading: boolean;
  roleLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  isSuperAdmin: boolean;
  isPlatformAdmin: boolean;
  userRole: UserRole | null;
}

const PlatformTenantContext = createContext<PlatformTenantContextValue | undefined>(undefined);

interface PlatformTenantProviderProps {
  children: ReactNode;
}

export const PlatformTenantProvider: React.FC<PlatformTenantProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [platformTenant, setPlatformTenant] = useState<PlatformTenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  const isSuperAdmin = userRole === 'super_admin';
  const isPlatformAdmin = userRole === 'platform_admin';

  const fetchUserRole = async (): Promise<UserRole | null> => {
    if (!user) {
      setUserRole(null);
      setRoleLoading(false);
      setIsLoading(false);
      return null;
    }

    setRoleLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_user_role', {
        _user_id: user.id
      });

      if (error) throw error;
      const role = data as UserRole;
      setUserRole(role);
      return role;
    } catch (err) {
      console.error('[PlatformTenantProvider] Error fetching user role:', err);
      setUserRole('user');
      return null;
    } finally {
      setRoleLoading(false);
    }
  };

  const fetchPlatformTenant = async (currentUserRole: UserRole | null, retryCount = 0) => {
    if (!user) {
      setPlatformTenant(null);
      setIsLoading(false);
      return;
    }

    const MAX_RETRIES = 2;
    const RETRY_DELAY = 1000;
    const isUserSuperAdmin = currentUserRole === 'super_admin';

    try {
      setIsLoading(true);
      setError(null);

      // For super admins, check localStorage for saved tenant selection
      if (isUserSuperAdmin) {
        const savedTenantId = localStorage.getItem('super_admin_selected_tenant_id');
        if (savedTenantId) {
          const { data: tenant, error: tenantError } = await supabase
            .from('platform_tenants')
            .select('*')
            .eq('id', savedTenantId)
            .single();

          if (tenantError) {
            localStorage.removeItem('super_admin_selected_tenant_id');
          } else {
            setPlatformTenant(tenant);
            setIsLoading(false);
            return;
          }
        } else {
          // No saved tenant = platform view
          setPlatformTenant(null);
          setIsLoading(false);
          return;
        }
      }

      // Get user's platform_tenant_id using the RPC function
      const { data: tenantId, error: rpcError } = await supabase
        .rpc('get_user_platform_tenant', {
          _user_id: user.id
        });

      if (rpcError) {
        if (retryCount < MAX_RETRIES && rpcError.message.includes('network')) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return fetchPlatformTenant(currentUserRole, retryCount + 1);
        }
        throw rpcError;
      }
      
      if (!tenantId) {
        setPlatformTenant(null);
        setIsLoading(false);
        return;
      }

      // Fetch full platform tenant details
      const { data: tenant, error: tenantError } = await supabase
        .from('platform_tenants')
        .select('*')
        .eq('id', tenantId)
        .single();

      if (tenantError) {
        if (retryCount < MAX_RETRIES && tenantError.message.includes('network')) {
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          return fetchPlatformTenant(currentUserRole, retryCount + 1);
        }
        throw tenantError;
      }

      setPlatformTenant(tenant);
      
      // Cache tenant data
      try {
        localStorage.setItem('cached_platform_tenant', JSON.stringify({
          tenant,
          timestamp: Date.now()
        }));
      } catch (cacheError) {
        // Ignore cache errors
      }
    } catch (err) {
      console.error('[PlatformTenantProvider] Error fetching platform tenant:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch platform tenant'));
      
      // Try cached data as fallback
      try {
        const cached = localStorage.getItem('cached_platform_tenant');
        if (cached) {
          const { tenant, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < 3600000) {
            setPlatformTenant(tenant);
          }
        }
      } catch (cacheError) {
        // Ignore cache errors
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = async () => {
    // Manual refresh triggered
    const role = await fetchUserRole();
    await fetchPlatformTenant(role);
  };

  // Listen for storage events to detect tenant selection changes from other components
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'super_admin_selected_tenant_id' && userRole === 'super_admin') {
        fetchPlatformTenant(userRole); // Storage change detected
        fetchPlatformTenant(userRole);
      }
    };

    // Also listen for custom event for same-tab updates
    const handleTenantSwitch = () => {
      // Tenant switch event received
      if (userRole === 'super_admin') {
        fetchPlatformTenant(userRole);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('tenant-switch', handleTenantSwitch);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('tenant-switch', handleTenantSwitch);
    };
  }, [userRole]);

  useEffect(() => {
    const initialize = async () => {
      // Fetch role and tenant in parallel for faster loading
      const [role] = await Promise.all([
        fetchUserRole(),
        // Pre-warm: start tenant fetch immediately using cached/default role assumption
      ]);
      await fetchPlatformTenant(role);
    };
    initialize();
  }, [user?.id]);

  const value: PlatformTenantContextValue = {
    platformTenant,
    isLoading,
    loading: isLoading,
    roleLoading,
    error,
    refresh,
    isSuperAdmin,
    isPlatformAdmin,
    userRole
  };

  return (
    <PlatformTenantContext.Provider value={value}>
      {children}
    </PlatformTenantContext.Provider>
  );
};

export const usePlatformTenant = () => {
  const context = useContext(PlatformTenantContext);
  if (context === undefined) {
    throw new Error('usePlatformTenant must be used within a PlatformTenantProvider');
  }
  return context;
};